import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  makeStyles,
  tokens,
  Button,
  MessageBar,
  shorthands,
  Spinner,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Badge,
  Tooltip
} from '@fluentui/react-components';
import { 
  ArrowDownload24Regular,
  ArrowClockwise24Regular,
  Document24Regular,
  Eye24Regular
} from '@fluentui/react-icons';
import { EmailSaveAPI } from '../../services/emailSaveApi';
import { EmailListItem, EmailPreviewResponse } from '../../types/email.types';
import { useIdentity } from '@workspace/identity-react';
import EmailPreviewModal from './EmailPreviewModal';

const useStyles = makeStyles({
  card: {
    ...shorthands.margin(tokens.spacingVerticalM),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
  },
  content: {
    ...shorthands.padding(tokens.spacingVerticalM),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  refreshButton: {
    marginLeft: 'auto',
  },
  table: {
    marginTop: tokens.spacingVerticalM,
  },
  emptyState: {
    textAlign: 'center',
    ...shorthands.padding(tokens.spacingVerticalXXL),
    color: tokens.colorNeutralForeground3,
  },
  downloadButton: {
    minWidth: 'auto',
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  formatBadge: {
    textTransform: 'uppercase',
  }
});

const EmailList: React.FC = () => {
  const styles = useStyles();
  const { getAccessToken } = useIdentity();
  const [api] = useState(() => new EmailSaveAPI(getAccessToken));
  
  const [emails, setEmails] = useState<EmailListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    fileId?: string;
    filename?: string;
  }>({ isOpen: false });
  const [previewData, setPreviewData] = useState<EmailPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    loadEmails();
  }, []);

  const loadEmails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.listEmails();
      setEmails(response.emails);
    } catch (err) {
      console.error('Failed to load emails:', err);
      setError('Failed to load saved emails');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileId: string) => {
    setDownloading(fileId);
    
    try {
      await api.downloadEmail(fileId);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Failed to download email');
    } finally {
      setDownloading(null);
    }
  };

  const handlePreview = async (fileId: string, filename: string) => {
    setPreviewModal({ isOpen: true, fileId, filename });
    setPreviewLoading(true);
    setPreviewData(null);
    
    try {
      const preview = await api.previewEmail(fileId);
      setPreviewData(preview);
    } catch (err) {
      console.error('Preview failed:', err);
      setError('Failed to load email preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewModal({ isOpen: false });
    setPreviewData(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getFormatDisplay = (format: string): string => {
    // Backend sends string values like "Eml", "Html", etc.
    return format || 'Unknown';
  };

  const getStorageDisplay = (storage: string): string => {
    // Backend sends string values like "FileStorage", "OneDrive", etc.
    const storageNames: Record<string, string> = {
      'FileStorage': 'Local',
      'OneDrive': 'OneDrive',
      'SharePoint': 'SharePoint',
      'GoogleDrive': 'Google Drive',
      'Dropbox': 'Dropbox'
    };
    return storageNames[storage] || storage || 'Unknown';
  };

  const getFormatColor = (format: string): "brand" | "important" | "informative" | "success" => {
    switch (format?.toLowerCase()) {
      case 'eml': return 'brand';
      case 'pdf': return 'important';
      case 'html': return 'informative';
      case 'msg': return 'success';
      default: return 'brand';
    }
  };

  const getStorageColor = (storage: string): "outline" | "tint" => {
    switch (storage) {
      case 'FileStorage': return 'outline';
      case 'OneDrive':
      case 'SharePoint':
      case 'GoogleDrive':
      case 'Dropbox':
        return 'tint';
      default: return 'outline';
    }
  };

  if (loading) {
    return (
      <Card className={styles.card}>
        <div className={styles.content}>
          <Spinner label="Loading saved emails..." />
        </div>
      </Card>
    );
  }

  return (
    <Card className={styles.card}>
      <CardHeader
        header={
          <div className={styles.header}>
            <h3>Saved Emails</h3>
            <Button
              className={styles.refreshButton}
              icon={<ArrowClockwise24Regular />}
              onClick={loadEmails}
              appearance="subtle"
              title="Refresh"
            />
          </div>
        }
        description={`${emails.length} saved email${emails.length !== 1 ? 's' : ''}`}
      />
      
      <div className={styles.content}>
        {error && (
          <MessageBar
            intent="error"
            style={{ marginBottom: tokens.spacingVerticalM }}
          >
            {error}
          </MessageBar>
        )}

        {emails.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No saved emails yet.</p>
            <p>Save an email from the Save Email tab to see it here.</p>
          </div>
        ) : (
          <Table className={styles.table} size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Filename</TableHeaderCell>
                <TableHeaderCell>Format</TableHeaderCell>
                <TableHeaderCell>Size</TableHeaderCell>
                <TableHeaderCell>Saved Date</TableHeaderCell>
                <TableHeaderCell>Storage</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {emails.map((email) => {
                return (
                  <TableRow key={email.fileId}>
                    <TableCell>
                      <Tooltip 
                        content={email.filename} 
                        relationship="label"
                        showDelay={250}
                        hideDelay={0}
                        positioning="above-start"
                      >
                        <TableCellLayout 
                          truncate
                          media={<Document24Regular />}
                        >
                          {email.filename}
                        </TableCellLayout>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        className={styles.formatBadge}
                        color={getFormatColor(email.format)}
                        appearance="filled"
                        size="small"
                      >
                        {getFormatDisplay(email.format).toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell>{email.formattedFileSize}</TableCell>
                    <TableCell>{formatDate(email.createdAt)}</TableCell>
                    <TableCell>
                      <Badge 
                        appearance={getStorageColor(email.storageLocation)}
                        size="small"
                      >
                        {getStorageDisplay(email.storageLocation)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        className={styles.downloadButton}
                        icon={<Eye24Regular />}
                        onClick={() => handlePreview(email.fileId, email.filename)}
                        appearance="subtle"
                        size="small"
                        title="Preview"
                      />
                      <Button
                        className={styles.downloadButton}
                        icon={<ArrowDownload24Regular />}
                        onClick={() => handleDownload(email.fileId)}
                        disabled={downloading === email.fileId}
                        appearance="subtle"
                        size="small"
                        title="Download"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <EmailPreviewModal
        isOpen={previewModal.isOpen}
        onClose={closePreview}
        preview={previewData}
        loading={previewLoading}
        filename={previewModal.filename}
      />
    </Card>
  );
};

export default EmailList;
